import {axiosInstance, axiosInstanceWithToken} from "../instances/axiosInstance";
import {AnswerRequest} from "./types/AnswerRequest";
import {ApiFailure, ApiSuccess} from "./types/ApiResponse";
import {AttachmentResponseModel} from "./types/AttachmentResponseModel";
import axios, {AxiosResponse} from "axios";
import {AnswerResponseModel} from "./types/AnswerResponseModel";
import {User} from "./types/User";
import {Applicant} from "./types/Applicant";
import {LoginResponse, LoginResponseTwoFactor} from "./types/LoginResponse";
import {API_URL} from "../App";
import {Party} from "./types/Party";
import {PartyType} from "./types/PatryType";
import {MatterType} from "./types/MatterType";
import {Client} from "./types/Client";

type ApiResponse<T, U = unknown> = ApiSuccess<T> | ApiFailure<U>

export class ApiService {
    static async GetParty(partyId: number, language: string = 'en') {
        return await ApiService.Handle<Party, null>(() => axiosInstanceWithToken.get(`party/${language}/${partyId}`));
    }

    static async Answer(data: AnswerRequest) {
        return await ApiService.Handle<AnswerResponseModel, null>(() => axiosInstanceWithToken.post(`answer`,
            data,
            {
                headers: {
                    "Content-type": "application/json"
                }
            }));
    }

    static async ModifiedAt(partyId: number) {
        return await ApiService.Handle<number, null>(() => axiosInstanceWithToken.get(`party/${partyId}/modified-at`));
    }

    static async GetUser() {
        return await ApiService.Handle<User, null>(() => axiosInstanceWithToken.get(`user`));
    }

    static async GetApplicant(applicantId: number) {
        return await ApiService.Handle<Applicant, null>(() => axiosInstanceWithToken.get(`applicant/${applicantId}`))
    }

    static async Collapse(value: boolean, partyId: number, presetId: number) {
        return await ApiService.Handle<null, null>(() =>
            axiosInstanceWithToken.post(`party/${partyId}/preset/${presetId}/collapse`, {value}));
    }

    static async Attachment(formData: FormData) {
        return await ApiService.Handle<AttachmentResponseModel, null>(() => axiosInstanceWithToken.post(`attachment`,
            formData,
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                    "Accept": "multipart/form-data",
                }
            }
        ));
    }

    static async Login(login: string, password: string, code?: string, twoFactorProvider?: number) {
        return await ApiService.Handle<LoginResponse, LoginResponseTwoFactor>(() =>
            axiosInstance.post("user/login", {
                login,
                password,
                code,
                twoFactorProvider
            }))
    }

    static async RefreshToken(accessToken: string, refreshToken: string) {
        return await ApiService.Handle<LoginResponse, null>(() => axiosInstance.post("token/refresh", {
            accessToken,
            refreshToken
        }))
    }

    static async GetActivationSms(login: string, password: string, phone: string) {
        return await ApiService.Handle<any, any>(() => axiosInstance.post(`user/two-factor/phone`,
            {
                login: login,
                password: password,
                phone: phone
            }
        ));
    }

    static async TwoFactor(login: string, password: string, code: string) {
        return await ApiService.Handle<LoginResponse, null>(() => axiosInstance.post(`user/two-factor`,
            {
                login: login,
                password: password,
                code: code
            }
        ));
    }

    static async GetSms(login: string, password: string, phone: string) {
        return await ApiService.Handle<any, any>(() => axiosInstance.post(`user/two-factor/sms`,
            {
                login: login,
                password: password,
                phone: phone
            }
        ));
    }

    static async PhoneConfirm(login: string, password: string, phone: string, code: string) {
        return await ApiService.Handle<LoginResponse, any>(() => axiosInstance.post(`user/two-factor/phone/confirm`,
            {
                login: login,
                password: password,
                phone: phone,
                code: code
            }
        ))
    }

    static async PhoneSkip(login: string, password: string) {
        return await ApiService.Handle<LoginResponse, any>(() => axiosInstance.post(`user/two-factor/phone/skip`,
            {
                login: login,
                password: password
            }
        ))
    }

    static async AuthenticatorSkip(login: string, password: string) {
        return await ApiService.Handle<LoginResponse, any>(() => axiosInstance.post(`user/two-factor/authenticator/skip`,
            {
                login: login,
                password: password
            }))
    }

    static async SubmitParty(partyId: number) {
        return await ApiService.Handle<any, null>(() => axiosInstanceWithToken.post(`party/${partyId}/submit`, null))
    }

    static async SetPassword(password: string, confirmPassword: string, token: string, unique: string) {
        return await ApiService.Handle<LoginResponse, any>(() => axiosInstance.post(`user/${unique}/password`, {
            password: password,
            confirmPassword: confirmPassword,
            token: token
        }));
    }

    static async CheckActivated(unique: string) {
        return await ApiService.Handle<boolean, null>(() => axiosInstance.get(`${API_URL}/user/${unique}/activated`));
    }

    static async GetAgentClients(agentId: number) {
        return await ApiService.Handle<Client[], null>(() => axiosInstanceWithToken.get(`agent/${agentId}/clients`));
    }

    static async GetAgentMatterTypes(agentId: number) {
        return await ApiService.Handle<MatterType[], null>(() => axiosInstanceWithToken.get(`agent/${agentId}/matter-types`));
    }

    static async GetAgentPartyTypes(agentId: number) {
        return await ApiService.Handle<PartyType[], null>(() => axiosInstanceWithToken.get(`agent/${agentId}/party-types`));
    }

    static async CreateClient(agentId: number, client: {
        name: string,
        partyTypeId: number,
        matterTypeId: number
    }) {
        return await ApiService.Handle<any, null>(() => axiosInstanceWithToken.post(`agent/${agentId}/clients`, client));
    }

    static async DeleteClient(agentId: number, clientId: number) {
        return await ApiService.Handle<any, null>(() => axiosInstanceWithToken.delete(`agent/${agentId}/clients/${clientId}`));
    }

    static async GetClientParties(agentId: number, clientId: number) {
        return await ApiService.Handle<any, null>(() => axiosInstanceWithToken.get(`agent/${agentId}/clients/${clientId}/parties`));
    }

    static async UpdateClientParty(agentId: number, clientId: number, partyId: number, payload: { name: string }) {
        return await ApiService.Handle<any, null>(() => axiosInstanceWithToken.patch(`/agent/${agentId}/clients/${clientId}/parties/${partyId}`, payload));
    }

    static async DeleteClientParty(agentId: number, clientId: number, partyId: number) {
        return await ApiService.Handle<any, null>(() => axiosInstanceWithToken.delete(`/agent/${agentId}/clients/${clientId}/parties/${partyId}`));
    }

    static async CreateClientParty(agentId: number, clientId: number, party: { name: string, partyTypeId: number }) {
        return await ApiService.Handle<any, null>(() => axiosInstanceWithToken.post(`/agent/${agentId}/clients/${clientId}/parties`, party));
    }

    private static async Handle<T, U>(func: () => Promise<AxiosResponse<ApiResponse<T, U>>>): Promise<ApiSuccess<T> | ApiFailure<U | null>> {
        try {
            const response = await func();
            response.data.statusCode = response.status;
            if (response.data.success) return response.data as ApiSuccess<T>;
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const statusCode = error.response?.status ?? 600;
                const errors =
                    (error.response?.data as ApiFailure<U> | undefined)?.errors ??
                    [error.response?.statusText ?? error.message ?? "Something went wrong"];
                const data = (error.response?.data as ApiFailure<U> | undefined)?.data ?? null;

                return {
                    success: false,
                    statusCode,
                    errors,
                    data
                };
            }
            return {
                success: false,
                statusCode: 600,
                errors: ["Something went wrong"],
                data: null
            };
        }
    }
}